import { useState } from "react";
import { Loader2 } from "lucide-react";
import { formatPaymentStatementDescriptor } from "@cpos/shared";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/base/dialog";
import { Button } from "@/components/ui/base/button";
import { Checkbox } from "@/components/ui/base/checkbox";
import { Input } from "@/components/ui/base/input";
import { Label } from "@/components/ui/base/label";
import type { MerchantsRepository } from "@/application/merchants/merchants_repository";
import {
  buildBusinessProfilePatch,
  diffBusinessInfo,
  draftFrom,
  effectsOf,
  normalizeDraft,
  validateBusinessInfo,
  type AddressDraft,
  type BusinessInfoField,
} from "@/application/merchants/business_info_edit";
import type { BusinessProfile } from "../types";
import { AddressFields } from "./info-edit/AddressFields";
import { Field } from "./info-edit/Field";
import { ResultStep } from "./info-edit/ResultStep";
import { ReviewChanges } from "./info-edit/ReviewChanges";
import { useInfoSave } from "./info-edit/useInfoSave";

type Step = "edit" | "review" | "result";

type Repo = Pick<
  MerchantsRepository,
  | "getBusinessProfile"
  | "updateBusinessInfo"
  | "writeInfoChangeAudit"
  | "awaitStripePush"
>;

const ACCEPTED_COPY = {
  headline: "Saved. Receipts, the app and Stripe are updated.",
  detail:
    "Terminals show it after Sync Now in Settings, or their next restart.",
};

const CHECKING_DETAIL = "Receipts and the app already have the new details.";

/** Receipts read `company` as a whole; the microsite falls back to the owner's address field by field. */
function noBusinessAddressNote(hasCompanyRecord: boolean, hasOwner: boolean) {
  if (!hasOwner) return "No business address on file. Saving creates one.";
  const shown = hasCompanyRecord
    ? "Receipts print no address and the public microsite shows the owner's personal address until one is added."
    : "Receipts and the public microsite show the owner's personal address until one is added.";
  return `No business address on file. ${shown} Saving creates a business address; the owner's personal address is not changed.`;
}

interface Props {
  business: BusinessProfile;
  /** `saved` is true when at least one save succeeded while the dialog was open. */
  onClose: (saved: boolean) => void;
  repo: Repo;
}

/**
 * Support edits a live merchant's name, support phone and address:
 * Edit → Review → Result. Mount only while open.
 */
export function EditBusinessInfoDialog({ business, onClose, repo }: Props) {
  const [baseline, setBaseline] = useState(() => draftFrom(business));
  const [draft, setDraft] = useState(baseline);
  const [step, setStep] = useState<Step>("edit");
  const [touched, setTouched] = useState<Set<BusinessInfoField>>(new Set());
  const [fraudAcknowledged, setFraudAcknowledged] = useState(false);
  const info = useInfoSave(repo, business);

  const changes = diffBusinessInfo(baseline, draft);
  const validation = validateBusinessInfo(baseline, draft);
  const needsFraudAck = validation.fraudTerm !== null;
  const changedFields = new Set(changes.map((c) => c.field));
  const highlight = (field: BusinessInfoField) =>
    changedFields.has(field) ? "border-blue-500" : undefined;
  const canReview =
    changes.length > 0 &&
    Object.keys(validation.errors).length === 0 &&
    (!needsFraudAck || fraudAcknowledged);
  const errorFor = (field: BusinessInfoField) =>
    // eslint-disable-next-line security/detect-object-injection -- field is a BusinessInfoField literal
    touched.has(field) ? validation.errors[field] : undefined;
  const touch = (field: BusinessInfoField) =>
    setTouched((prev) => new Set(prev).add(field));
  const setAddress = (patch: Partial<AddressDraft>) =>
    setDraft((d) => ({ ...d, address: { ...d.address, ...patch } }));

  function close() {
    if (info.saving) return;
    onClose(info.saved);
  }

  async function handleSave() {
    const row = await info.save({
      changes,
      // The address is merged into the whole nested record, which the update
      // processor rewrites (company.phone), so merge into the live row.
      readLiveRow: changes.some((c) => c.field === "address"),
      buildPatch: (base) => buildBusinessProfilePatch(base, draft, changes),
      update: (patch) => repo.updateBusinessInfo(patch),
      audit: {
        action: "merchant.business_info_changed",
        extra: { fraudWarningAcknowledged: needsFraudAck },
      },
    });
    if (!row) return;
    const fresh = draftFrom(row);
    setBaseline(fresh);
    setDraft(fresh);
    setTouched(new Set());
    setFraudAcknowledged(false);
    setStep("result");
  }

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent className="sm:max-w-lg">
        {step === "edit" && (
          <>
            <DialogHeader>
              <DialogTitle>Edit business info</DialogTitle>
              <p className="text-muted-foreground text-sm">
                Merchant: {baseline.businessName || business.id}
              </p>
              <DialogDescription>
                Changes receipts, the merchant's apps and Stripe.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <Field
                id="business-name"
                label="Business name (DBA)"
                error={errorFor("businessName")}
                hint={`Card statements will read: ${formatPaymentStatementDescriptor(normalizeDraft(draft).businessName)}`}
              >
                {(describedBy) => (
                  <Input
                    id="business-name"
                    value={draft.businessName}
                    className={highlight("businessName")}
                    aria-invalid={!!errorFor("businessName")}
                    aria-describedby={describedBy}
                    onChange={(e) => {
                      setFraudAcknowledged(false);
                      setDraft({ ...draft, businessName: e.target.value });
                    }}
                    onBlur={() => touch("businessName")}
                  />
                )}
              </Field>

              {needsFraudAck && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  <p className="font-medium">
                    "{validation.fraudTerm}" is on the protected brand list.
                  </p>
                  <p>Check this is the merchant's real trading name.</p>
                  <div className="mt-2 flex items-center gap-2">
                    <Checkbox
                      id="fraud-ack"
                      checked={fraudAcknowledged}
                      onCheckedChange={(v) => setFraudAcknowledged(v === true)}
                    />
                    <Label htmlFor="fraud-ack" className="text-xs">
                      I've confirmed the name is genuine
                    </Label>
                  </div>
                </div>
              )}

              <Field
                id="support-phone"
                label="Support phone"
                error={errorFor("supportPhone")}
                hint="Printed on receipts"
              >
                {(describedBy) => (
                  <Input
                    id="support-phone"
                    type="tel"
                    value={draft.supportPhone}
                    className={highlight("supportPhone")}
                    aria-invalid={!!errorFor("supportPhone")}
                    aria-describedby={describedBy}
                    onChange={(e) =>
                      setDraft({ ...draft, supportPhone: e.target.value })
                    }
                    onBlur={() => touch("supportPhone")}
                  />
                )}
              </Field>

              <fieldset
                className="space-y-2"
                aria-describedby={
                  errorFor("address") ? "business-address-error" : undefined
                }
                onBlur={() => touch("address")}
              >
                <legend className="text-foreground mb-1 text-xs font-medium">
                  Business address
                </legend>
                {!baseline.address.line1.trim() && (
                  <p className="text-muted-foreground text-xs">
                    {noBusinessAddressNote(
                      baseline.hasCompanyRecord,
                      !!business.individual,
                    )}
                  </p>
                )}
                <AddressFields
                  value={draft.address}
                  changed={changedFields.has("address")}
                  onChange={setAddress}
                />
                {errorFor("address") && (
                  <p
                    id="business-address-error"
                    className="text-xs text-red-600"
                  >
                    {errorFor("address")}
                  </p>
                )}
              </fieldset>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button disabled={!canReview} onClick={() => setStep("review")}>
                Review changes
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "review" && (
          <>
            <DialogHeader>
              <DialogTitle>Review changes</DialogTitle>
              <DialogDescription>
                {changes.length} field{changes.length === 1 ? "" : "s"} changed.
              </DialogDescription>
            </DialogHeader>

            <ReviewChanges changes={changes} effects={effectsOf(changes)} />

            {info.saveError && (
              <p role="alert" className="text-sm text-red-600">
                Couldn't save: {info.saveError}. Nothing changed.
              </p>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                disabled={info.saving}
                onClick={() => {
                  info.clearSaveError();
                  setStep("edit");
                }}
              >
                Back
              </Button>
              <Button disabled={info.saving} onClick={() => void handleSave()}>
                {info.saving && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save changes
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "result" && (
          <ResultStep
            title="Business info saved"
            changes={info.savedChanges}
            outcome={info.stripe}
            acceptedCopy={ACCEPTED_COPY}
            checkingDetail={CHECKING_DETAIL}
            auditOk={info.auditOk}
            retryingAudit={info.retryingAudit}
            onRetryAudit={() => void info.retryAudit()}
            onEditAgain={() => setStep("edit")}
            onDone={close}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
